import { REGEX_CONSTANTS } from '@components-shared/constants';
import { keyMap } from '@locales/lokalise/keyMap';

const MAXIMUM_IDENTICAL_CONSECUTIVE_CHARS = 2;

type StrengthRuleType = {
  message: string;
  fn: (value: string) => boolean;
};

export type StrengthRulesType = {
  minLength: StrengthRuleType;
  hasLowercase: StrengthRuleType;
  hasUppercase: StrengthRuleType;
  hasNumber: StrengthRuleType;
  hasSpecialCharacters: StrengthRuleType;
  hasRepeatedCharacters: StrengthRuleType;
};

const hasNumber = (value: string): boolean => new RegExp(/[0-9]/).test(value);

const hasUppercase = (value: string): boolean => new RegExp(/[A-Z]/).test(value);

const hasLowercase = (value: string): boolean => new RegExp(/[a-z]/).test(value);

const minLength = (value: string): boolean => value.length > 7;

const hasSpecialCharacters = (value: string): boolean =>
  new RegExp(/[~`!@#$%^&*()_+\-=[\]\\{}|;':"",./<>?]/).test(value);

const hasRepeatedCharacters = (value: string): boolean => {
  let repeated = 0;

  for (let i = 0; i < value.length - 1; i++) {
    repeated = value[i] === value[i + 1] ? repeated + 1 : 0;
    if (repeated >= MAXIMUM_IDENTICAL_CONSECUTIVE_CHARS) {
      return false;
    }
  }

  return true;
};

export const strengthRules: StrengthRulesType = {
  minLength: { message: keyMap.formsPasswordStrengthMinLength, fn: minLength },
  hasLowercase: { message: keyMap.formsPasswordStrengthHasLowercase, fn: hasLowercase },
  hasUppercase: { message: keyMap.formsPasswordStrengthHasUppercase, fn: hasUppercase },
  hasNumber: { message: keyMap.formsPasswordStrengthHasNumber, fn: hasNumber },
  hasSpecialCharacters: { message: keyMap.formsPasswordStrengthHasSpecialCharacters, fn: hasSpecialCharacters },
  hasRepeatedCharacters: { message: 'No repeat more than 2', fn: hasRepeatedCharacters },
};

export const strengthIndicator = (value: string): string[] => {
  const strengths: string[] = [];

  Object.keys(strengthRules).map((r) => {
    if (strengthRules[r as keyof StrengthRulesType].fn(value)) {
      strengths.push(r);
    }
  });

  return strengths;
};

export const strengthValidate = (value: string): boolean =>
  strengthIndicator(value).length === Object.keys(strengthRules).length;

export const newPasswordIsDifferFromOldOneValidate = (password: string, oldPassword?: string) => {
  return password === '' || password !== oldPassword;
};

export const isValidOnlyEngWithDigitsAndSpecialSymbols = (value: string): boolean => {
  const regexPattern = REGEX_CONSTANTS.ONLY_ENG_DIGITS_SPECIAL_CHARS_PATTERN;

  return new RegExp(regexPattern).test(value);
};
